// Copyright (c) StrangeBee 2024

import React, { ReactElement, useContext, useEffect, useMemo } from 'react';
import { Form, FormInstance, Select } from 'antd';
import { Store } from 'antd/lib/form/interface';
import { convertToLocaleId, useLocalMessage } from '@Helpers/';
import {
    CaseOutput,
    CaseCustomField,
    CaseStatus,
    TaskOutput,
    CustomStatusMapType,
    CustomStatusOutput,
    CaseStatusStage,
} from 'thehive-sdk';
import { THRadio } from '@Molecules/Inputs/THRadio/THRadio';
import { THForm } from '@Molecules/THForm/THForm';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { CaseStatusTasks } from './CaseStatusTasks';
import { CFValueItem } from '@Molecules/CFList/CFValueItem';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { CustomColorLabel } from '@Atoms/CustomColorLabel/CustomColorLabel';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { ScopeContext } from '@Context/ScopeProvider';

export interface CaseStatusValues extends Store {
    resolutionStatus: string;
    impactStatus?: string;
    summary: string;
    customFields?: CaseCustomField[];
}

interface CaseStatusFormProps {
    caseId?: string;
    taskList?: TaskOutput[];
    onFinish(values: CaseStatusValues): void;
    initialValues: Partial<CaseOutput>;
    statusList?: CustomStatusOutput[];
    closed?: boolean;
    form: FormInstance;
    isValid: boolean;
    onChange: () => void;
    onCancel: () => void;
}

const { OptGroup } = Select;

export const CaseStatusForm = ({
    taskList = [],
    onFinish,
    initialValues,
    statusList = [],
    closed = false,
    form,
    isValid,
    onChange,
    onCancel,
    caseId,
}: CaseStatusFormProps): ReactElement | null => {
    const i = useLocalMessage();

    const { customFieldList } = useContext(ScopeContext);
    const stageRef = closed ? CaseStatusStage.CLOSED : CaseStatusStage.IN_PROGRESS;
    const [caseStatus, setCaseStatus] = React.useState<CustomStatusMapType>({});
    const caseCustomFields = Form.useWatch<CaseCustomField[]>(['customFields'], form);

    const mandatoryCustomFields = useMemo(
        () =>
            (initialValues?.customFields || [])?.filter(({ value, name }) =>
                customFieldList?.find(
                    ({ mandatory, name: cfName }) =>
                        cfName === name && mandatory && (value === undefined || value === null),
                ),
            ),
        [initialValues?.customFields, customFieldList],
    );

    const impactStatusOptions = useMemo(
        () => [
            { label: i('general.yes'), value: 'WithImpact' },
            { label: i('general.no'), value: 'NoImpact' },
        ],
        [i],
    );

    const onFinishHandler = (values: CaseStatusValues) => {
        onFinish(values);
    };

    useEffect(() => {
        if (statusList.length) {
            const statusMap = statusList.reduce((acc, curr) => {
                if (curr.stage !== stageRef) {
                    return acc;
                }
                if (!acc[curr.stage]) {
                    acc[curr.stage] = [];
                }
                acc[curr.stage].push(curr);
                return acc;
            }, {} as CustomStatusMapType);
            setCaseStatus(statusMap);
        }
    }, [statusList, stageRef]);

    const mandatoryTasks = useMemo(
        () => taskList.filter(({ mandatory, extraData }) => !!mandatory && !extraData?.logCount),
        [taskList],
    );

    const mandatory = useMemo(() => {
        return closed && !!mandatoryTasks.length;
    }, [closed, mandatoryTasks]);

    return (
        <THForm<CaseStatusValues>
            name="case-status-form"
            onValuesChange={onChange}
            form={form}
            onFinish={onFinishHandler}
            initialValues={{ ...initialValues, status: undefined }}
            submitTouchedValues={['customFields']}
        >
            <THForm.Layout
                footer={
                    <FormSubmitButton
                        onCancel={onCancel}
                        footerText={
                            taskList?.length && closed ? i('case.close.form.submit_and_close') : i('general.confirm')
                        }
                        disabled={!isValid || mandatory}
                    />
                }
            >
                {!!taskList?.length && closed && (
                    <CaseStatusTasks
                        taskList={mandatory ? mandatoryTasks : taskList}
                        modeMandatory={mandatory}
                        caseId={caseId}
                    />
                )}

                {statusList && !mandatory ? (
                    <THForm.Item
                        name="status"
                        label={i('case.close.form.status.label')}
                        rules={[{ required: true, message: i('case.close.form.status.required') }]}
                    >
                        <THSelect placeholder={initialValues.status}>
                            {Object.entries(caseStatus).map(([key, values]) => (
                                <OptGroup key={key} label={key}>
                                    {values.map(({ value, colour }) => (
                                        <THSelect.Option key={value} value={value}>
                                            <CustomColorLabel
                                                value={i({
                                                    id: `case.data.status.${convertToLocaleId(value)}`,
                                                    defaultMessage: value,
                                                })}
                                                color={colour}
                                            />
                                        </THSelect.Option>
                                    ))}
                                </OptGroup>
                            ))}
                        </THSelect>
                    </THForm.Item>
                ) : null}

                {closed && !mandatory && (
                    <>
                        <THForm.Item<CaseStatusValues>
                            noStyle
                            shouldUpdate={(prev, curr): boolean => prev.status !== curr.status}
                        >
                            {({ getFieldValue }) => (
                                <THForm.Item
                                    name="impactStatus"
                                    label={i('case.close.form.impactStatus.label')}
                                    rules={[
                                        {
                                            required: closed && getFieldValue('status') === CaseStatus.TRUE_POSITIVE,
                                            message: i('case.close.form.impactStatus.required'),
                                        },
                                    ]}
                                    hidden={getFieldValue('status') !== CaseStatus.TRUE_POSITIVE}
                                >
                                    <THRadio.Group>
                                        {impactStatusOptions.map(({ label, value }) => (
                                            <THRadio key={value} value={value}>
                                                {label}
                                            </THRadio>
                                        ))}
                                    </THRadio.Group>
                                </THForm.Item>
                            )}
                        </THForm.Item>

                        <THForm.Item
                            name="summary"
                            label={i('case.close.form.summary.label')}
                            rules={[{ required: true, message: i('case.close.form.summary.required') }]}
                        >
                            <THMarkdown sameSize />
                        </THForm.Item>

                        <THForm.List name="customFields">
                            {(fields) => {
                                return caseCustomFields?.map((customField, index) => (
                                    <CFValueItem
                                        key={customField?._id}
                                        customField={customField}
                                        fieldName={fields[index]?.name}
                                        borderless={false}
                                        hidden={!mandatoryCustomFields.find(({ _id }) => customField._id === _id)}
                                        mandatory={!!mandatoryCustomFields.find(({ _id }) => customField._id === _id)}
                                    />
                                ));
                            }}
                        </THForm.List>
                    </>
                )}
            </THForm.Layout>
        </THForm>
    );
};
